import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import { LAYERS_NORMALIZED_MAX } from "./layerGeometry";
import { exportBinaryMaskFromCanvas } from "./eraseLayerObject";

export const OBJECT_ERASER_BRUSH_FRACTION_MIN = 0.03;
export const OBJECT_ERASER_BRUSH_FRACTION_MAX = 0.4;
export const OBJECT_ERASER_BRUSH_FRACTION_DEFAULT = 0.1;

const MASK_QUALITY = 2;
const SHADE_FILL = "#000000";
const SHADE_OPACITY = 0.32;
const ERASER_OVERLAY_Z_INDEX = 100;
const MASK_UNDO_LIMIT = 30;

const isUndoHotkey = (event) =>
  (event.ctrlKey || event.metaKey) &&
  !event.shiftKey &&
  !event.altKey &&
  (event.code === "KeyZ" || event.key?.toLowerCase() === "z");

const isEditableKeyboardTarget = (target) => {
  if (!(target instanceof HTMLElement)) return false;
  const tagName = target.tagName;
  return (
    tagName === "INPUT" ||
    tagName === "TEXTAREA" ||
    tagName === "SELECT" ||
    target.isContentEditable
  );
};

const cloneCanvasImage = (canvas) => {
  const context = canvas?.getContext("2d");
  if (!context || !canvas.width || !canvas.height) return null;
  return context.getImageData(0, 0, canvas.width, canvas.height);
};

const restoreCanvasImage = (canvas, imageData) => {
  const context = canvas?.getContext("2d");
  if (!context || !imageData) return;
  context.putImageData(imageData, 0, 0);
};

const eventToMaskPoint = (event, canvas) => {
  const rect = canvas.getBoundingClientRect();
  if (!rect.width || !rect.height) {
    return { x: 0, y: 0 };
  }
  return {
    x: ((event.clientX - rect.left) / rect.width) * canvas.width,
    y: ((event.clientY - rect.top) / rect.height) * canvas.height,
  };
};

const paintStroke = ({ context, from, to, radius }) => {
  context.lineCap = "round";
  context.lineJoin = "round";
  context.strokeStyle = SHADE_FILL;
  context.fillStyle = SHADE_FILL;
  context.globalAlpha = 1;
  context.lineWidth = radius * 2;
  if (!from) {
    context.beginPath();
    context.arc(to.x, to.y, radius, 0, Math.PI * 2);
    context.fill();
    return;
  }
  context.beginPath();
  context.moveTo(from.x, from.y);
  context.lineTo(to.x, to.y);
  context.stroke();
};

const ObjectEraserOverlay = forwardRef(
  (
    {
      layer,
      pixelBox,
      canvasWidth,
      canvasHeight,
      brushFraction,
      locked = false,
      onHasMaskChange = () => {},
    },
    ref
  ) => {
    const canvasRef = useRef(null);
    const cursorRef = useRef(null);
    const lastPointRef = useRef(null);
    const hasMaskRef = useRef(false);
    const undoStackRef = useRef([]);
    const onHasMaskChangeRef = useRef(onHasMaskChange);
    onHasMaskChangeRef.current = onHasMaskChange;

    const maskSize = useMemo(() => {
      const compositionWidth = Math.max(
        1,
        ((Number(layer?.width) || 0) / LAYERS_NORMALIZED_MAX) *
          (Number(canvasWidth) || 0)
      );
      const compositionHeight = Math.max(
        1,
        ((Number(layer?.height) || 0) / LAYERS_NORMALIZED_MAX) *
          (Number(canvasHeight) || 0)
      );
      const minEdge = 256;
      const quality = Math.max(
        MASK_QUALITY,
        minEdge / Math.max(1, Math.min(compositionWidth, compositionHeight))
      );
      return {
        width: Math.max(1, Math.round(compositionWidth * quality)),
        height: Math.max(1, Math.round(compositionHeight * quality)),
      };
    }, [canvasHeight, canvasWidth, layer?.height, layer?.width]);

    const brushRadiusMask =
      brushFraction * Math.min(maskSize.width, maskSize.height);
    const brushDiameterScreen =
      brushFraction * Math.min(pixelBox.width, pixelBox.height) * 2;

    useImperativeHandle(
      ref,
      () => ({
        exportMask: () => exportBinaryMaskFromCanvas(canvasRef.current),
      }),
      []
    );

    useEffect(() => {
      hasMaskRef.current = false;
      undoStackRef.current = [];
      onHasMaskChangeRef.current(false);
      const canvas = canvasRef.current;
      const context = canvas?.getContext("2d");
      if (context) {
        context.clearRect(0, 0, canvas.width, canvas.height);
      }
    }, [layer?.id]);

    useEffect(() => {
      const undoLastStroke = () => {
        const canvas = canvasRef.current;
        const snapshot = undoStackRef.current.pop();
        if (!canvas || !snapshot) return false;
        restoreCanvasImage(canvas, snapshot);
        if (!undoStackRef.current.length) {
          hasMaskRef.current = false;
          onHasMaskChangeRef.current(false);
        }
        return true;
      };

      const onKeyDown = (event) => {
        if (locked) return;
        if (!isUndoHotkey(event) || isEditableKeyboardTarget(event.target)) {
          return;
        }
        if (lastPointRef.current) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        undoLastStroke();
      };

      window.addEventListener("keydown", onKeyDown, true);
      return () => window.removeEventListener("keydown", onKeyDown, true);
    }, [locked]);

    const moveCursor = (event) => {
      const cursor = cursorRef.current;
      if (!cursor) return;
      cursor.style.opacity = "1";
      cursor.style.left = `${event.clientX}px`;
      cursor.style.top = `${event.clientY}px`;
    };

    const hideCursor = () => {
      const cursor = cursorRef.current;
      if (!cursor) return;
      cursor.style.opacity = "0";
    };

    const onPointerDown = (event) => {
      if (locked) return;
      if (event.pointerType === "mouse" && event.button !== 0) return;
      const canvas = canvasRef.current;
      const context = canvas?.getContext("2d");
      if (!canvas || !context) return;
      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.setPointerCapture(event.pointerId);
      const snapshot = cloneCanvasImage(canvas);
      if (snapshot) {
        undoStackRef.current.push(snapshot);
        if (undoStackRef.current.length > MASK_UNDO_LIMIT) {
          undoStackRef.current.shift();
        }
      }
      const point = eventToMaskPoint(event, canvas);
      paintStroke({
        context,
        from: null,
        to: point,
        radius: brushRadiusMask,
      });
      lastPointRef.current = point;
      if (!hasMaskRef.current) {
        hasMaskRef.current = true;
        onHasMaskChange(true);
      }
      moveCursor(event);
    };

    const onPointerMove = (event) => {
      if (locked) return;
      moveCursor(event);
      const canvas = canvasRef.current;
      const context = canvas?.getContext("2d");
      if (!canvas || !context) return;
      if (!event.currentTarget.hasPointerCapture?.(event.pointerId)) return;
      const point = eventToMaskPoint(event, canvas);
      paintStroke({
        context,
        from: lastPointRef.current,
        to: point,
        radius: brushRadiusMask,
      });
      lastPointRef.current = point;
      if (!hasMaskRef.current) {
        hasMaskRef.current = true;
        onHasMaskChange(true);
      }
    };

    const onPointerUp = (event) => {
      if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      lastPointRef.current = null;
    };

    if (!pixelBox?.width || !pixelBox?.height) return null;

    return (
      <>
        <div
          style={{
            position: "absolute",
            left: pixelBox.x,
            top: pixelBox.y,
            width: pixelBox.width,
            height: pixelBox.height,
            zIndex: ERASER_OVERLAY_Z_INDEX,
            overflow: "hidden",
            cursor: locked ? "wait" : "none",
            touchAction: "none",
            pointerEvents: locked ? "none" : "auto",
          }}
        >
          <canvas
            ref={canvasRef}
            width={maskSize.width}
            height={maskSize.height}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onPointerEnter={moveCursor}
            onPointerLeave={(event) => {
              if (!event.currentTarget.hasPointerCapture?.(event.pointerId)) {
                hideCursor();
              }
            }}
            style={{
              width: "100%",
              height: "100%",
              display: "block",
              touchAction: "none",
              opacity: SHADE_OPACITY,
            }}
          />
        </div>
        {createPortal(
          <div
            ref={cursorRef}
            style={{
              position: "fixed",
              width: brushDiameterScreen,
              height: brushDiameterScreen,
              marginLeft: -brushDiameterScreen / 2,
              marginTop: -brushDiameterScreen / 2,
              borderRadius: "50%",
              boxSizing: "border-box",
              border: "2px solid #fff",
              boxShadow:
                "0 0 0 1.5px #18181B, inset 0 0 0 1px rgba(24,24,27,0.35)",
              backgroundColor: "rgba(24, 24, 27, 0.18)",
              pointerEvents: "none",
              zIndex: 2000,
              opacity: 0,
            }}
          />,
          document.body
        )}
      </>
    );
  }
);

ObjectEraserOverlay.displayName = "ObjectEraserOverlay";

export default ObjectEraserOverlay;
